import { FolderFilled } from '@/assets/icons'
import { Tag } from '@/components/tag/Tag'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { kExternalStorageProvider, type ExternalStorageProvider } from '@/constants/constants-external-storage'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { timestampMsToDateTimeParts } from '@/util/time'

const PROVIDER_ICON_CLASS_NAMES: Record<ExternalStorageProvider, string> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: 'size-2.5',
  [kExternalStorageProvider.DROPBOX]: 'size-2.5',
  [kExternalStorageProvider.BOX]: 'h-[7px] w-[14px]',
  [kExternalStorageProvider.ONEDRIVE]: 'h-2 w-3',
  [kExternalStorageProvider.CLIO]: 'size-2.5',
  [kExternalStorageProvider.SHAREPOINT]: 'size-2.5',
}

interface DisconnectedStorageItemProps {
  provider: ExternalStorageProvider
}

interface DisconnectedFromProviderTagProps extends DisconnectedStorageItemProps {
  disconnectedAt: string
}

/** Provider-marked folder icon for a DMS folder retained after disconnect. */
export function DisconnectedFolderIcon({ provider }: DisconnectedStorageItemProps) {
  const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider]

  return (
    <span className="relative size-6 shrink-0" data-testid="dms-disconnected-folder-icon" data-provider={provider} aria-hidden>
      <FolderFilled className="absolute inset-0 size-6 text-true-gray-300" />
      <span className="absolute left-[5px] top-2 flex h-3 w-[14px] items-center justify-center grayscale brightness-50">
        <ProviderIcon data-testid="dms-disconnected-provider-logo" className={PROVIDER_ICON_CLASS_NAMES[provider]} />
      </span>
    </span>
  )
}

/** Figma tonal tag identifying the provider from which a retained item was disconnected. */
export function DisconnectedFromProviderTag({ provider, disconnectedAt }: DisconnectedFromProviderTagProps) {
  const providerLabel = PROVIDER_COMPACT_DISPLAY_NAME[provider]
  const { date: disconnectedDate } = timestampMsToDateTimeParts(Date.parse(disconnectedAt))

  return (
    <CustomizableTooltip
      describeChild
      disableInteractive
      offset={[-2, 4]}
      placement="bottom-start"
      tooltipClassName="!m-0 w-[371px] !max-w-[calc(100vw-32px)] !rounded-lg !bg-brand-neutral-50 !p-0 !font-sans shadow-[0_0.6px_12.5px_0_rgba(0,0,0,0.10),0_10px_5px_0_rgba(0,0,0,0.04)]"
      title={
        <div className="flex flex-col gap-2 rounded-md border border-brand-neutral-100 bg-white p-3">
          <p className="text-sm font-normal tracking-[-0.15px] text-brand-neutral-950">
            These files stay in this matter and remain searchable. They no longer update from {providerLabel}. Reconnecting the same folder will resume syncing.
          </p>
          <p className="text-xs font-medium text-brand-neutral-600">Disconnected {disconnectedDate}</p>
        </div>
      }
    >
      <span tabIndex={0} className="inline-flex rounded focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500">
        <Tag variant="tonal" color="Black" size="2xs">
          Disconnected from {providerLabel}
        </Tag>
      </span>
    </CustomizableTooltip>
  )
}
